import React from "react";
import { motion } from "framer-motion";
import { useNavigate } from "react-router-dom";
import usePageTitle from "../hooks/usePageTitle";
import { FaArrowLeft } from "react-icons/fa";

const CookiePolicy = () => {
  const navigate = useNavigate();
  usePageTitle("Cookie Policy");
  
  const containerVariants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: {
        delayChildren: 0.3,
        staggerChildren: 0.1,
      },
    },
  };

  const itemVariants = {
    hidden: { y: 50, opacity: 0 },
    visible: {
      y: 0,
      opacity: 1,
      transition: {
        duration: 0.6,
        ease: "easeOut",
      },
    },
  };

  return (
    <div className="min-h-screen relative overflow-hidden">
      <div className="relative z-10">
        {/* Header */}
        <motion.div
          className="container mx-auto px-4 py-8"
          variants={containerVariants}
          initial="hidden"
          animate="visible"
        >
          <motion.div
            variants={itemVariants}
            className="flex items-center gap-4 mb-8"
          >
            <motion.button
              onClick={() => navigate("/")}
              className="flex items-center gap-2 text-romantic-pink hover:text-white transition-colors duration-300"
              whileHover={{ x: -5 }}
              whileTap={{ scale: 0.95 }}
            >
              <FaArrowLeft className="text-xl" />
              <span className="font-semibold">Back to Home</span>
            </motion.button>
          </motion.div>

          <motion.div variants={itemVariants} className="text-center mb-12">
            <div className="flex justify-center items-center gap-4 flex-col">
              <div className="text-center lg:text-left">
                <img
                  src="/assets/moon-logo.png"
                  alt="Logo"
                  className="w-32 mx-auto lg:mx-0"
                />
              </div>
              <div className="text-center lg:text-left">
                <img
                  src="/assets/png-logo.png"
                  alt="Logo"
                  className="w-40 mx-auto lg:mx-0 pb-2"
                />
              </div>
            </div>
            <h1 className="text-4xl md:text-6xl font-bold text-white mb-4">
              Cookie Policy
            </h1>
            <p className="text-xl text-gray-300 max-w-3xl mx-auto">
              Your privacy matters to us. Here's how we use cookies to enhance your experience.
            </p>
          </motion.div>
        </motion.div>

        <motion.div 
          variants={containerVariants}
          className="container mx-auto px-4 pb-16 max-w-5xl"
        >
          <motion.div 
            variants={itemVariants}
            className="bg-gray-900/80 backdrop-blur-lg border border-gray-700/50 rounded-2xl p-6 md:p-10 space-y-8"
          >
            <p className="text-gray-300">
              Welcome to Datenite — where your connections matter, and so does your privacy. This Cookie Policy explains how we use cookies and similar technologies on our website and app. We keep it transparent, simple, and safe… just like dating should be. ❤️
            </p>

            <div className="space-y-6">
              <div>
                <h2 className="text-2xl font-bold text-romantic-pink mb-3">1. What Are Cookies?</h2>
                <p className="text-gray-300">
                  Cookies are small text files stored on your device when you visit a website or use an app. Think of them as tiny digital crumbs that help us remember your preferences and improve your experience.
                </p>
              </div>

              <div>
                <h2 className="text-2xl font-bold text-romantic-pink mb-3">2. Why Does Datenite Use Cookies?</h2>
                <p className="text-gray-300 mb-4">
                  We use cookies to make your experience smoother, safer, and more personalised — basically, we try to avoid "awkward first date" energy.
                </p>
                <p className="text-gray-300">
                  Datenite uses cookies for:
                </p>
                <ul className="list-disc pl-6 space-y-2 mt-2 text-gray-300">
                  <li><span className="font-semibold">Essential Functionality</span> - To keep the website/app running properly (login, security, navigation).</li>
                  <li><span className="font-semibold">Analytics & Performance</span> - To understand how users interact with the platform so we can improve features, speed, and overall usability.</li>
                  <li><span className="font-semibold">Personalisation</span> - To show content, matches, and suggestions based on your interests.</li>
                  <li><span className="font-semibold">Advertising & Marketing</span> - To show relevant ads, measure campaign performance, and improve conversions.</li>
                </ul>
              </div>

              <div>
                <h2 className="text-2xl font-bold text-romantic-pink mb-3">3. Types of Cookies We Use</h2>
                
                <h3 className="text-xl font-semibold text-white mt-4 mb-2">A. Strictly Necessary Cookies</h3>
                <p className="text-gray-300 mb-4">
                  These cookies are essential for the website or app to function. Without them, things break.
                  <br />
                  <span className="text-gray-400">Examples: login sessions, security protection, account verification.</span>
                </p>

                <h3 className="text-xl font-semibold text-white mt-4 mb-2">B. Performance & Analytics Cookies</h3>
                <p className="text-gray-300 mb-4">
                  Used to track how users navigate Datenite. They help us fix issues, improve speed, and understand what features are loved (or ignored).
                  <br />
                  <span className="text-gray-400">Common providers: Google Analytics, Meta Pixel, Firebase Analytics</span>
                </p>

                <h3 className="text-xl font-semibold text-white mt-4 mb-2">C. Functional Cookies</h3>
                <p className="text-gray-300 mb-4">
                  These cookies help personalise your experience — like remembering your preferences, location settings, or app behaviour.
                </p>

                <h3 className="text-xl font-semibold text-white mt-4 mb-2">D. Advertising & Targeting Cookies</h3>
                <p className="text-gray-300">
                  Used to deliver ads relevant to you. Also helps reduce repetitive ads and measure how well our campaigns perform.
                  <br />
                  <span className="text-gray-400">These may be used by: Google Ads, Social media platforms, Third-party ad partners</span>
                </p>
              </div>

              <div>
                <h2 className="text-2xl font-bold text-romantic-pink mb-3">4. Do We Use Third-Party Cookies?</h2>
                <p className="text-gray-300">
                  Yes. Some cookies come from trusted third-party services that help us analyze performance, run ads, prevent fraud, and improve user flows. But we do not share your personal information with advertisers without your consent.
                </p>
              </div>

              <div>
                <h2 className="text-2xl font-bold text-romantic-pink mb-3">5. How Long Do Cookies Stay on My Device?</h2>
                <p className="text-gray-300">
                  Cookies can be:
                </p>
                <ul className="list-disc pl-6 space-y-2 mt-2 text-gray-300">
                  <li><span className="font-semibold">Session Cookies</span> - Deleted automatically when you close the app or browser.</li>
                  <li><span className="font-semibold">Persistent Cookies</span> - Stay on your device until they expire or you delete them manually.</li>
                </ul>
                <p className="text-gray-300 mt-2">
                  We use both.
                </p>
              </div>

              <div>
                <h2 className="text-2xl font-bold text-romantic-pink mb-3">6. How Can You Control or Delete Cookies?</h2>
                <p className="text-gray-300">
                  You're the boss — you can manage cookies anytime. Options include:
                </p>
                <ul className="list-disc pl-6 space-y-2 mt-2 text-gray-300">
                  <li>Updating cookie preferences via our Cookie Banner</li>
                  <li>Adjusting settings in your browser</li>
                  <li>Clearing cache/cookies manually</li>
                  <li>Opting out of analytics and advertising services</li>
                </ul>
                <p className="text-gray-300 mt-2 italic">
                  If you block essential cookies, parts of the site or app may not work properly.
                </p>
              </div>

              <div>
                <h2 className="text-2xl font-bold text-romantic-pink mb-3">7. Do We Track Your Location?</h2>
                <p className="text-gray-300">
                  Datenite uses location strictly for nearby matches, local content, and app functionality. Location tracking requires your permission and can be turned off anytime in your device settings.
                </p>
              </div>

              <div>
                <h2 className="text-2xl font-bold text-romantic-pink mb-3">8. Cookies on the Datenite App</h2>
                <p className="text-gray-300">
                  Our mobile app also uses device identifiers, performance SDKs, crash analytics, and security tools. These help improve safety, speed, and user experience.
                </p>
              </div>

              <div>
                <h2 className="text-2xl font-bold text-romantic-pink mb-3">9. Updates to This Policy</h2>
                <p className="text-gray-300">
                  We may update this Cookie Policy occasionally to stay compliant with regulations and improve transparency. When we do, we'll update the "Last Updated" date.
                </p>
              </div>

              <div>
                <h2 className="text-2xl font-bold text-romantic-pink mb-3">10. Contact Us</h2>
                <p className="text-gray-300">
                  If you have questions about this Cookie Policy or want the techy details:
                </p>
                <p className="text-gray-300 mt-2">
                  Datenite Support Team
                  <br />
                  📧 Email: support@datenite.co.uk
                  <br />
                  🌐 Website: <a href="https://datenite.co.uk" className="text-romantic-pink hover:underline">https://datenite.co.uk</a>
                </p>
              </div>
            </div>
          </motion.div>
        </motion.div>
      </div>
    </div>
  );
};

export default CookiePolicy;
